import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

function AgeBadge({ days }) {
  if (days === null) return <span className="age-badge grey">Unknown</span>;
  if (days <= 14) return <span className="age-badge green">{days}d</span>;
  if (days <= 30) return <span className="age-badge blue">{days}d</span>;
  if (days <= 60) return <span className="age-badge amber">{days}d ⚠</span>;
  if (days <= 90) return <span className="age-badge orange">{days}d ⚠⚠</span>;
  return <span className="age-badge red">{days}d 🔴</span>;
}

export default function JobsPipeline({ jobs, weeklyStats, entity }) {
  const [sortBy, setSortBy] = useState('age');
  const [filterStatus, setFilterStatus] = useState('initiated');
  const [filterClient, setFilterClient] = useState('');

  const openJobs = jobs.filter(j => {
    if (filterStatus === 'initiated' && j.status !== 'Initiated') return false;
    if (filterStatus === 'completed' && j.status !== 'Completed') return false;
    if (filterClient && !j.client.toLowerCase().includes(filterClient.toLowerCase())) return false;
    return true;
  });

  const sorted = [...openJobs].sort((a, b) => {
    if (sortBy === 'age') return (b.daysOpen || 0) - (a.daysOpen || 0);
    if (sortBy === 'value') return b.estRev - a.estRev;
    if (sortBy === 'client') return a.client.localeCompare(b.client);
    return 0;
  });

  // Stats
  const totalOpen = jobs.filter(j => j.status === 'Initiated').length;
  const stale30 = jobs.filter(j => j.status === 'Initiated' && j.daysOpen > 30).length;
  const stale60 = jobs.filter(j => j.status === 'Initiated' && j.daysOpen > 60).length;
  const totalOpenValue = jobs.filter(j => j.status === 'Initiated').reduce((s, j) => s + j.estRev, 0);

  // End-of-month bunching
  const today = new Date();
  const dayOfMonth = today.getDate();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate(); // eslint-disable-line
  const lastThreeDays = jobs.filter(j => { if (!j.createdDate) return false; const d = new Date(j.createdDate); return (dayOfMonth - d.getDate()) <= 3 && d.getMonth() === today.getMonth(); }).length;
  const thisMonthJobs = jobs.filter(j => { if (!j.createdDate) return false; return new Date(j.createdDate).getMonth() === today.getMonth(); }).length;
  const bunchingPct = thisMonthJobs > 0 ? Math.round(lastThreeDays / thisMonthJobs * 100) : 0;

  // Weekly chart data
  const chartData = weeklyStats.map(w => ({
    name: w.label,
    opened: w.opened,
    closed: w.closed,
  }));

  // Jobs by client (top 10 open)
  const byClient = {};
  jobs.filter(j => j.status === 'Initiated').forEach(j => {
    byClient[j.client] = (byClient[j.client] || 0) + 1;
  });
  const topClients = Object.entries(byClient)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, count]) => ({ name: name.length > 25 ? name.slice(0, 25) + '…' : name, count }));

  return (
    <div className="view jobs-pipeline">
      {/* KPI row */}
      <div className="kpi-row">
        <div className="kpi">
          <span className="kpi-value">{totalOpen}</span>
          <span className="kpi-label">Open jobs</span>
          <span className="kpi-sub">AED {(totalOpenValue / 1000).toFixed(0)}K est. value</span>
        </div>
        <div className={`kpi ${stale30 > 0 ? 'warn' : ''}`}>
          <span className="kpi-value">{stale30}</span>
          <span className="kpi-label">Open 30+ days</span>
        </div>
        <div className={`kpi ${stale60 > 0 ? 'danger' : ''}`}>
          <span className="kpi-value">{stale60}</span>
          <span className="kpi-label">Open 60+ days 🔴</span>
        </div>
        {bunchingPct > 35 && (
          <div className="kpi warn">
            <span className="kpi-value">{bunchingPct}%</span>
            <span className="kpi-label">Month-end bunching ⚠</span>
            <span className="kpi-sub">{lastThreeDays} of {thisMonthJobs} jobs in last 3 days</span>
          </div>
        )}
      </div>

      {/* Charts row */}
      <div className="charts-row">
        <div className="chart-card">
          <h3 className="chart-title">Jobs opened vs closed — weekly</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} barGap={2}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="opened" name="Opened" fill="#0e7c7b" radius={[3,3,0,0]} />
              <Bar dataKey="closed" name="Closed" fill="#c9a84c" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3 className="chart-title">Open jobs by client (top 10)</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={topClients} layout="vertical" barSize={14}>
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={130} />
              <Tooltip />
              <Bar dataKey="count" name="Open jobs" radius={[0,3,3,0]}>
                {topClients.map((_, i) => (
                  <Cell key={i} fill={i === 0 ? '#ef4444' : i <= 2 ? '#f97316' : '#0e7c7b'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Table */}
      <div className="table-section">
        <div className="table-controls">
          <div className="filter-pills">
            {[
              { id: 'initiated', label: `Open (${jobs.filter(j=>j.status==='Initiated').length})` },
              { id: 'completed', label: `Completed (${jobs.filter(j=>j.status==='Completed').length})` },
              { id: 'all', label: 'All' },
            ].map(f => (
              <button key={f.id} className={`pill ${filterStatus===f.id?'active':''}`} onClick={() => setFilterStatus(f.id)}>{f.label}</button>
            ))}
          </div>
          <input
            className="search-input small"
            placeholder="Filter by client..."
            value={filterClient}
            onChange={e => setFilterClient(e.target.value)}
          />
          <div className="sort-pills">
            <span className="sort-label">Sort:</span>
            {[
              { id: 'age', label: 'Age' },
              { id: 'value', label: 'Value' },
              { id: 'client', label: 'Client' },
            ].map(s => (
              <button key={s.id} className={`pill small ${sortBy===s.id?'active':''}`} onClick={() => setSortBy(s.id)}>{s.label}</button>
            ))}
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Job #</th>
                <th>Type</th>
                <th>Opened</th>
                <th className="right">Est Rev</th>
                <th className="right">Act Rev</th>
                <th className="right">GP%</th>
                <th>Status</th>
                <th>Age</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((job, i) => (
                <tr key={i} className={job.daysOpen > 60 ? 'row-critical' : job.daysOpen > 30 ? 'row-warn' : ''}>
                  <td className="client-cell">{job.client}</td>
                  <td className="mono">{job.jobNo}</td>
                  <td><span className="job-type-tag">{job.jobType}</span></td>
                  <td>{job.createdDate ? new Date(job.createdDate).toLocaleDateString('en-GB', {day:'2-digit',month:'short',year:'2-digit'}) : '—'}</td>
                  <td className="right">{job.estRev > 0 ? `AED ${job.estRev.toLocaleString('en',{maximumFractionDigits:0})}` : '—'}</td>
                  <td className="right">{job.actRev > 0 ? `AED ${job.actRev.toLocaleString('en',{maximumFractionDigits:0})}` : '—'}</td>
                  <td className={`right ${job.gpPct === null ? '' : job.gpPct >= 20 ? 'good' : job.gpPct >= 15 ? 'warn' : 'danger'}`}>
                    {job.gpPct !== null ? `${job.gpPct.toFixed(1)}%` : '—'}
                  </td>
                  <td><span className={`status-pill ${job.status === 'Completed' ? 'completed' : 'initiated'}`}>{job.status}</span></td>
                  <td><AgeBadge days={job.daysOpen} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
